import React, { useEffect, useRef, useState, useCallback } from "react";
import { ElectricianPuzzleProps, GameStats } from "../../types";
import { DIFFICULTY_CONFIG } from "../../constants/difficulty";
import { PuzzleProvider, usePuzzleContext } from "../../context/PuzzleContext";
import { useTimer } from "../../hooks/useTimer";
import { PuzzleGrid } from "./PuzzleGrid";
import ElectricianHeader from "./ElectricianHeader";
import ElectricianGameProgress from "./ElectricianGameProgress";
import { ResultOverlay } from "./ResultOverlay";
import { useAudioEffects } from "../../../../hooks/use-audio-effects";
import winSound from "../../assets/audio/win.mp3";
import loseSound from "../../assets/audio/lose.mp3";

function ElectricianPuzzleInner({
  difficulty = "easy",
  onWin,
  onLose,
  onClose,
}: Omit<ElectricianPuzzleProps, "autoStart">) {
  const config = DIFFICULTY_CONFIG[difficulty];
  const [resultStats, setResultStats] = useState<GameStats | null>(null);
  const [resultWon, setResultWon] = useState<boolean | null>(null);
  const [shaking, setShaking] = useState(false);
  const startTimeRef = useRef(Date.now());
  const { playEffect } = useAudioEffects();

  const {
    grid,
    liveMap,
    distanceMap,
    phase,
    clicks,
    rotateTile,
    resetGame,
    handleLose,
  } = usePuzzleContext();

  const clicksRef = useRef(clicks);
  clicksRef.current = clicks;

  const { timeRemaining, isRunning, start, pause, reset } = useTimer(
    config.timeLimit,
    handleLose,
  );

  // Sync timer start/stop with phase
  useEffect(() => {
    if (phase === "playing" && !isRunning) {
      start();
    } else if (phase !== "playing" && isRunning) {
      pause();
    }
  }, [phase]); // eslint-disable-line

  // Win detection
  useEffect(() => {
    if (phase === "won") {
      playEffect(winSound, { volume: 1 });
      pause();
      const elapsed = Math.round((Date.now() - startTimeRef.current) / 1000);
      const stats: GameStats = {
        difficulty,
        totalClicks: clicksRef.current,
        timeElapsed: elapsed,
        timeRemaining,
        won: true,
      };
      setResultWon(true);
      setResultStats(stats);
      onWin?.(stats);
    }
  }, [phase]); // eslint-disable-line

  // Lose detection
  useEffect(() => {
    if (phase === "lost") {
      playEffect(loseSound, { volume: 1 });
      const elapsed = Math.round((Date.now() - startTimeRef.current) / 1000);
      const stats: GameStats = {
        difficulty,
        totalClicks: clicksRef.current,
        timeElapsed: elapsed,
        timeRemaining: 0,
        won: false,
      };
      setResultStats(stats);
      setResultWon(false);
      setShaking(true);
      setTimeout(() => setShaking(false), 600);
      onLose?.(stats);
    }
  }, [phase]); // eslint-disable-line

  const handleRetry = useCallback(() => {
    setResultWon(null);
    setResultStats(null);
    reset(config.timeLimit);
    startTimeRef.current = Date.now();
    resetGame();
  }, [config.timeLimit, reset, resetGame]);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.7)" }}
    >
      <div className="flex flex-col gap-4 p-5 rounded-2xl bg-gradient-to-r from-[#161b26]/95 via-[#2c364a]/90 to-[#161b26]/95 w-[450px] 1440px:w-[600px] 2560px:w-[800px]">
        <div className="border-[1px] border-white/15 rounded-2xl p-5">
          <ElectricianHeader onClose={onClose} />
          <ElectricianGameProgress
            className="my-2"
            timeLeft={timeRemaining}
            clicks={clicks}
            difficulty={difficulty}
          />

          <div className="relative rounded-2xl overflow-hidden">
            <PuzzleGrid
              grid={grid}
              liveMap={liveMap}
              distanceMap={distanceMap}
              onTileClick={rotateTile}
              won={phase === "won"}
              shaking={shaking}
            />

            <ResultOverlay
              won={resultWon}
              stats={resultStats}
              onRetry={handleRetry}
              onExit={onClose}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export const ElectricianPuzzle = React.memo(function ElectricianPuzzle({
  difficulty = "easy",
  onWin,
  onLose,
  onClose,
  autoStart = true,
}: ElectricianPuzzleProps) {
  return (
    <PuzzleProvider
      difficulty={difficulty}
      onWin={onWin}
      onLose={onLose}
      autoStart={autoStart}
    >
      <ElectricianPuzzleInner
        difficulty={difficulty}
        onWin={onWin}
        onLose={onLose}
        onClose={onClose}
      />
    </PuzzleProvider>
  );
});
